/**
 * Routes and the auth gate.
 *
 * WHY the store list sits behind one gate component: /auth/me is the only way to know whether
 * the HttpOnly session cookie is still valid, so every authenticated route waits on that single
 * answer rather than each screen discovering a 401 on its own.
 */
import type { ReactNode } from "react";
import { BrowserRouter, Link, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { Loading } from "./components/States";
import { Billing } from "./pages/Billing";
import { Discrepancies } from "./pages/Discrepancies";
import { Disputes } from "./pages/Disputes";
import { FeeSchedule } from "./pages/FeeSchedule";
import { HeldFunds } from "./pages/HeldFunds";
import { Login } from "./pages/Login";
import { Overview } from "./pages/Overview";
import { PayoutDetail } from "./pages/PayoutDetail";
import { Payouts } from "./pages/Payouts";
import { StoreLayout } from "./pages/StoreLayout";
import { Stores } from "./pages/Stores";
import { Unsettled } from "./pages/Unsettled";
import { SessionProvider, useSession } from "./session";
import { ConnectionsProvider } from "./stores";

export function App(): ReactNode {
  return (
    <SessionProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route element={<RequireAuth />}>
            <Route path="/" element={<Stores />} />
            <Route path="/stores/:id" element={<StoreLayout />}>
              <Route index element={<Overview />} />
              <Route path="payouts" element={<Payouts />} />
              <Route path="payouts/:batchId" element={<PayoutDetail />} />
              <Route path="discrepancies" element={<Discrepancies />} />
              <Route path="disputes" element={<Disputes />} />
              <Route path="held-funds" element={<HeldFunds />} />
              <Route path="unsettled" element={<Unsettled />} />
              <Route path="fee-schedule" element={<FeeSchedule />} />
              <Route path="billing" element={<Billing />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </SessionProvider>
  );
}

function RequireAuth(): ReactNode {
  const { session } = useSession();
  if (session.status === "loading") return <div className="page centered"><Loading label="Signing you in" /></div>;
  if (session.status === "anonymous") return <Navigate to="/login" replace />;
  return <ConnectionsProvider><Outlet /></ConnectionsProvider>;
}

function NotFound(): ReactNode {
  return (
    <div className="page centered">
      <section className="card">
        <p className="state-title">Page not found</p>
        <Link className="btn" to="/">Back to your stores</Link>
      </section>
    </div>
  );
}
